/**
 * Route Registration
 *
 * Helper functions for registering route extensions
 */

import { ComponentType } from 'react';
import {
  RouteExtension,
  RouteMetadata,
  RouteGuard,
  UIPermission,
} from '../types';
import { getRouteRegistry } from './route-registry';

/**
 * Route registration options
 */
export interface RegisterRouteOptions {
  /**
   * Unique route ID
   */
  id: string;

  /**
   * Plugin ID
   */
  pluginId: string;

  /**
   * Route path
   */
  path: string;

  /**
   * Route component
   */
  component: ComponentType<any> | (() => Promise<{ default: ComponentType<any> }>);

  /**
   * Route name/title
   */
  name?: string;

  /**
   * Route description
   */
  description?: string;

  /**
   * Required permissions
   */
  permissions?: UIPermission[];

  /**
   * Whether route requires authentication
   */
  requiresAuth?: boolean;

  /**
   * Parent route ID
   */
  parentId?: string;

  /**
   * Child routes
   */
  children?: RouteExtension[];

  /**
   * Route metadata
   */
  metadata?: RouteMetadata;

  /**
   * Whether route is exact match only
   */
  exact?: boolean;

  /**
   * Route guard function
   */
  guard?: RouteGuard;

  /**
   * Route order
   */
  order?: number;
}

/**
 * Register a route extension
 *
 * @param options - Route registration options
 */
export const registerRoute = (options: RegisterRouteOptions): void => {
  const registry = getRouteRegistry();

  const route: RouteExtension = {
    id: options.id,
    pluginId: options.pluginId,
    path: options.path,
    component: options.component,
    name: options.name,
    description: options.description,
    permissions: options.permissions,
    requiresAuth: options.requiresAuth ?? false,
    parentId: options.parentId,
    children: options.children,
    metadata: options.metadata,
    exact: options.exact ?? false,
    guard: options.guard,
    order: options.order ?? 0,
  };

  registry.register(route);
};

/**
 * Unregister a route extension
 *
 * @param routeId - Route ID to unregister
 */
export const unregisterRoute = (routeId: string): void => {
  const registry = getRouteRegistry();
  registry.unregister(routeId);
};

/**
 * Get a route extension by ID
 *
 * @param routeId - Route ID
 * @returns Route extension or undefined
 */
export const getRoute = (routeId: string): RouteExtension | undefined => {
  const registry = getRouteRegistry();
  return registry.getRoute(routeId);
};

/**
 * Get route by path
 *
 * @param path - Route path
 * @returns Route extension or undefined
 */
export const getRouteByPath = (path: string): RouteExtension | undefined => {
  const registry = getRouteRegistry();
  return registry.getRouteByPath(path);
};

/**
 * Get all registered routes
 *
 * @returns Array of route extensions
 */
export const getAllRoutes = (): RouteExtension[] => {
  const registry = getRouteRegistry();
  return registry.getAllRoutes();
};

/**
 * Get routes by plugin ID
 *
 * @param pluginId - Plugin ID
 * @returns Array of route extensions
 */
export const getRoutesByPlugin = (pluginId: string): RouteExtension[] => {
  const registry = getRouteRegistry();
  return registry.getRoutesByPlugin(pluginId);
};

/**
 * Get root routes (no parent)
 *
 * @returns Array of root routes
 */
export const getRootRoutes = (): RouteExtension[] => {
  const registry = getRouteRegistry();
  return registry.getRootRoutes();
};

/**
 * Get child routes for a parent route
 *
 * @param parentId - Parent route ID
 * @returns Array of child routes
 */
export const getChildRoutes = (parentId: string): RouteExtension[] => {
  const registry = getRouteRegistry();
  return registry.getChildRoutes(parentId);
};
